import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { create } from 'zustand';

import AMP_KEYS from '@/configs/amp-track-keys';
import type { HeySynclyAnswer } from '@/entities/hey-syncly';
import useAgentStore from '@/features/Agent/stores';
import { getIntegrationQueryKeys } from '@/features/Setting/Integrations/integrationUtils';
import { useStudioListUpdateTrigger } from '@/features/Studio/atom/studioListUpdateTriggerAtom';
import type { STUDIO_ITEM_STATUS } from '@/features/Studio/model';
import service from '@/services';
import { SOCKET_SYNC_TYPE } from '@/types/common';
import type { SOURCE_TYPE } from '@/types/feedbackConst';
import type {
  ReanalyzeStatus,
  TemplateDetectionStatus,
  TemplateDTO,
} from '@/types/filterItems';
import { PREDICTION_STATE } from '@/types/taxonomyConst';
import { logger } from '@/utils/logger';
import { track } from '@/utils/track/track';
import { timeOut } from '@/utils/util';

import useCompanyStore from '../features/Account/store';
import useCustomerStore from './CustomerStore';
import useDashboard2Store from './Dashboard2Store';
import useExportStore from './ExportStore';
import useFeedbackListStore from './Feedback/FeedbackListStore';
import useFilterItemsStore from './FilterItemsStore';
import useGlobalSearchStore from './GlobalSearchStore';
import useHeySynclyStore from './HeySynclyStore';
import useTagStore from './TagStore';
import { usePredictionStateUpdate } from './TaxonomyStore';
import useUserStore from './UserStore';
import useWorkspaceStore from './WorkspaceStore';

type State = {
  socket: null | any;
  socketConnected: number;
};

type Actions = {
  setSocketConnected: (data: number) => void;
  setSocket: (socket: any) => void;
  sendMessage: (msg: string, value: any) => void;
  initSocketStore: () => void;
};

const initialState: State = {
  socket: null,
  socketConnected: 0,
};

// WORKSPACE
export const SOCKET_WORKSPACE = 'socket_workspace';

// FEEDBACK CRUD
export const SOCKET_SET_ADD_FEEDBACK = 'socket_set_add_feedback';
export const SOCKET_SET_PROCESSED_FEEDBACK = 'socket_set_processed_feedback';
export const SOCKET_SET_DELETE_FEEDBACK = 'socket_set_delete_feedback';

// FEEDBACK PROPERTIES
export const SOCKET_SET_CHANGE_FEEDBACK_COMPANY =
  'socket_set_change_feedback_comapny';
export const SOCKET_SET_CHANGE_FEEDBACK_PRIORITY =
  'socket_set_change_feedback_priority';
export const SOCKET_SET_CHANGE_FEEDBACK_DATAORIGIN =
  'socket_set_change_feedback_dataorigin';
export const SOCKET_SET_CHANGE_FEEDBACK_MANAGER =
  'socket_set_change_feedback_manager';

// LABEL PROPERTIES
const SOCKET_ADD_CHILD_LABEL = 'socket_add_child_label';
const SOCKET_REMOVE_LABEL = 'socket_remove_label';
const SOCKET_CHANGE_LABEL_NAME = 'socket_change_label_name';
const SOCKET_CHANGE_LABEL_COLOR = 'socket_change_label_color';

// SOCKET 통합작업 230411
const SOCKET_CUSTOMER = 'socket_customer';
const SOCKET_COMPANY = 'socket_company';
const SOCKET_MANAGER = 'socket_set_add_manager';
const SOCKET_ATTRIBUTE_CHANGED = 'socket_attribute_changed';
const SOCKET_ATTRIBUTE_MULTIPLE_CHANGED = 'socket_attribute_multiple_changed';

const SOCKET_CLASSIFICATION = 'socket_classification';

const SOCKET_PROJECT = 'socket_project';
const SOCKET_SUBPROJECT = 'socket_subproject';

// [Deprecated] preset
export const SOCKET_SET_ADD_PRESET = 'socket_set_add_preset';
export const SOCKET_SET_DELETE_PRESET = 'socket_set_delete_preset';

// change Tag
export const SOCKET_CHANGE_TAG = 'socket_tag_label';
export const SOCKET_UNCHANGE_TAG = 'socket_untag_label';

export const SOCKET_DOWNLOAD_LOG = 'socket_download_log';

// GPT V2 (ASK SYNLCY AI)
export const SOCKET_ASK_SYNLCY_AI = 'socket_ask_syncly_ai';

// Search
export const SOCKET_SEARCH = 'socket_search';

// Filter-Items
export const SOCKET_FILTER_ITEMS_TEMPLATE_DETECTION =
  'socket_filter_items_template_detection';
export const SOCKET_FILTER_ITEMS_REANALYZE = 'socket_filter_items_reanalyze';

// Taxonomy, Topic2

export const SOCKET_TAXONOMY_PREDICTION_STATE =
  'socket_taxonomy_prediction_state';
export const SOCKET_TOPIC2_PREDICTION_STATE = 'socket_topic2_prediction_state';

export const SOCKET_CHART_ANALYSIS = 'socket_chart_analysis';

export const SOCKET_HEY_SYNCLY_CHAT = 'socket_hey_syncly_chat';
export const SOCKET_HEY_SYNCLY_STUDIO_ITEM = 'socket_hey_syncly_studio_item';

// Integration
export const SOCKET_INTEGRATION_INFO = 'socket_integration_info';

export const SOCKET_LIST = [
  SOCKET_WORKSPACE,

  SOCKET_SET_ADD_FEEDBACK,
  SOCKET_SET_PROCESSED_FEEDBACK,
  SOCKET_SET_DELETE_FEEDBACK,
  SOCKET_SET_CHANGE_FEEDBACK_COMPANY,
  SOCKET_SET_CHANGE_FEEDBACK_PRIORITY,
  SOCKET_SET_CHANGE_FEEDBACK_DATAORIGIN,
  SOCKET_SET_ADD_PRESET,
  SOCKET_SET_DELETE_PRESET,
  SOCKET_ADD_CHILD_LABEL,
  SOCKET_REMOVE_LABEL,
  SOCKET_CHANGE_LABEL_NAME,
  SOCKET_CHANGE_LABEL_COLOR,
  SOCKET_SET_CHANGE_FEEDBACK_MANAGER,
  SOCKET_CLASSIFICATION,

  SOCKET_CUSTOMER,
  SOCKET_COMPANY,
  SOCKET_MANAGER,
  SOCKET_ATTRIBUTE_CHANGED,
  SOCKET_ATTRIBUTE_MULTIPLE_CHANGED,
  SOCKET_PROJECT,
  SOCKET_SUBPROJECT,
  SOCKET_CHANGE_TAG,
  SOCKET_UNCHANGE_TAG,

  SOCKET_SEARCH,
  SOCKET_DOWNLOAD_LOG,

  SOCKET_FILTER_ITEMS_REANALYZE,
  SOCKET_FILTER_ITEMS_TEMPLATE_DETECTION,

  SOCKET_TAXONOMY_PREDICTION_STATE,
  SOCKET_TOPIC2_PREDICTION_STATE,

  SOCKET_ASK_SYNLCY_AI,
  SOCKET_CHART_ANALYSIS,
  SOCKET_HEY_SYNCLY_CHAT,
  SOCKET_HEY_SYNCLY_STUDIO_ITEM,

  SOCKET_INTEGRATION_INFO,
];

// export const SOCKET_SET_SEND_FEEDBACK = 'socket_set_send_beedback';
const useSocketStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setSocketConnected: (data) => {
    set(() => ({ socketConnected: data }));
  },
  setSocket: (socket) => {
    set((state) => ({ ...state, socket }));
  },
  sendMessage: (msg, value) => {
    const { socket } = get();
    socket &&
      socket.emit(
        'data',
        JSON.stringify({
          msg: msg,
          value: value,
        }),
      );
  },
  initSocketStore() {
    // set({ socketConnected: 0 });
    // set(initialState);
  },
}));

const useSocketActions = () => {
  const queryClient = useQueryClient();
  const [_, setStudioListUpdateTrigger] = useStudioListUpdateTrigger();
  const { updateTaxonomyPredictionState, updateTopicPredictionState } =
    usePredictionStateUpdate();
  const socketOnServerMessageHandler = useCallback(
    async (type: string, data: string) => {
      const value = JSON.parse(data);
      logger.log('[Socket] : ', type);
      logger.log(value);
      switch (type) {
        case SOCKET_WORKSPACE: {
          const params: SOCKET_SYNC_STRUCT<WorkspaceT> = value;
          switch (params.type) {
            case SOCKET_SYNC_TYPE.UPDATE: {
              const { setWorkspaceAttr } = useWorkspaceStore.getState();
              setWorkspaceAttr(
                params.alias,
                params.field,
                params.payload[params.field],
              );
            }
          }
          break;
        }
        case SOCKET_CHANGE_TAG: {
          // update(추가)
          const { addTagInFeedback } = useFeedbackListStore.getState();
          addTagInFeedback(value.feedback, value.label);
          break;
        }
        case SOCKET_UNCHANGE_TAG: {
          // unchange 되는 부분
          const { removeTagInFeedback } = useFeedbackListStore.getState();

          removeTagInFeedback(value.feedback, value.label);
          break;
        }
        case SOCKET_SET_ADD_FEEDBACK: {
          const { setSendFeedback } = useFeedbackListStore.getState();
          setSendFeedback(value);
          break;
        }
        case SOCKET_SET_PROCESSED_FEEDBACK: {
          const { setSendFeedback } = useFeedbackListStore.getState();
          setSendFeedback(value, true);
          break;
        }
        case SOCKET_SET_DELETE_FEEDBACK: {
          const { setRemoveFeedback } = useFeedbackListStore.getState();
          setRemoveFeedback(value.alias);
          break;
        }
        case SOCKET_SET_CHANGE_FEEDBACK_COMPANY: {
          const { setNewProperty } = useFeedbackListStore.getState();
          setNewProperty(value.feedbackAlias, value.companyKey, 'company_info');
          break;
        }
        case SOCKET_SET_CHANGE_FEEDBACK_PRIORITY: {
          const { setNewProperty } = useFeedbackListStore.getState();
          setNewProperty(value.feedbackAlias, value.priorityKey, 'priority');
          break;
        }
        case SOCKET_SET_CHANGE_FEEDBACK_DATAORIGIN: {
          const { setNewProperty } = useFeedbackListStore.getState();
          setNewProperty(value.feedbackAlias, value.data_origin, 'data_origin');
          break;
        }
        case SOCKET_SET_CHANGE_FEEDBACK_MANAGER: {
          const { setNewProperty } = useFeedbackListStore.getState();
          setNewProperty(value.feedbackAlias, value.managerAlias, 'manager');
          break;
        }

        case SOCKET_ADD_CHILD_LABEL: {
          const { addTagInState } = useTagStore.getState();
          addTagInState(
            value.parent || '000000000000000000000000',
            value.alias,
            value.name,
            value.color,
          );
          break;
        }
        case SOCKET_REMOVE_LABEL: {
          const { deleteTagInState } = useTagStore.getState();
          deleteTagInState(value.alias);
          break;
        }
        case SOCKET_CHANGE_LABEL_NAME:
        case SOCKET_CHANGE_LABEL_COLOR: {
          const { changeTagInfoInState } = useTagStore.getState();
          changeTagInfoInState(value.alias, value.name, value.color);
          break;
        }
        case SOCKET_INTEGRATION_INFO: {
          const currWorkspace = useWorkspaceStore.getState().currWorkspace;
          if (!currWorkspace) return;
          const params: SOCKET_SYNC_STRUCT<{
            type: SOURCE_TYPE;
            inf: any;
          }> = value;

          queryClient.refetchQueries({
            queryKey: getIntegrationQueryKeys(params.payload.type, currWorkspace.alias),
          });
          break;
        }
        case SOCKET_COMPANY: {
          const { setCompanyAttr } = useCompanyStore.getState();
          const params: SOCKET_SYNC_STRUCT<CompanyT> = value;
          if (params.payload === null) {
            setCompanyAttr(params.alias, params.field, null);
          } else {
            const value = params.payload[params.field];
            if (!value) return; // Do nothing when value is undefined
            setCompanyAttr(params.alias, params.field, value);
          }
          break;
        }

        case SOCKET_CUSTOMER: {
          const { setCustomerAttr } = useCustomerStore.getState();
          const params: SOCKET_SYNC_STRUCT<UpdatableCustomerAttrs> = value;
          setCustomerAttr(
            params.alias,
            params.field,
            params.payload?.[params.field],
          );
          break;
        }

        case SOCKET_MANAGER: {
          const { setAgentAttr, setNewAgent, setDeleteAgent } =
            useAgentStore.getState();
          const params: SOCKET_SYNC_STRUCT<AgentT> = value;
          switch (params.type) {
            case SOCKET_SYNC_TYPE.CREATE:
              setNewAgent(params.payload);
              return;
            case SOCKET_SYNC_TYPE.UPDATE:
              setAgentAttr(
                params.alias,
                params.field,
                params.payload[params.field],
              );
              return;
            case SOCKET_SYNC_TYPE.DELETE:
              setDeleteAgent(params.alias);
              return;
            default:
              return;
          }
        }

        case SOCKET_ATTRIBUTE_CHANGED: {
          type WorkspaceFieldOption = {
            account_type: string;
            contact_type: string;
            data_origin: string;
            industry: string;
            job_function: string;
            plan: string;
            rating: string;
            role: string;
            size: string;
          };
          const params: SOCKET_SYNC_STRUCT<WorkspaceFieldOption> = value;
          const { setWorkspaceFieldOptions } = useWorkspaceStore.getState();
          if (params.type === SOCKET_SYNC_TYPE.DELETE) return;
          setWorkspaceFieldOptions(
            params.type,
            params.field,
            params.payload[params.field],
          );
          break;
        }

        case SOCKET_ATTRIBUTE_MULTIPLE_CHANGED: {
          type WorkspaceFieldOptions = {
            account_type: string[];
            contact_type: string[];
            data_origin: string[];
            industry: string[];
            job_function: string[];
            plan: string[];
            rating: string[];
            role: string[];
            size: string[];
          };
          const params: SOCKET_SYNC_STRUCT<WorkspaceFieldOptions> = value;
          const { setMultipleWorkspaceFieldOptions } =
            useWorkspaceStore.getState();
          if (params.type === SOCKET_SYNC_TYPE.DELETE) break;
          setMultipleWorkspaceFieldOptions(
            params.type,
            params.field,
            params.payload[params.field],
          );
          break;
        }
        case SOCKET_DOWNLOAD_LOG: {
          const params: SOCKET_SYNC_STRUCT<ExportHistoryItem> = value;
          if (params.type === SOCKET_SYNC_TYPE.UPDATE) {
            const user = useUserStore.getState().user;
            if (user?.user_id === params.payload.user) {
              const data = {
                ...params.payload,
              };
              useExportStore.getState().onExportComplete(data);
            }
          }
          break;
        }
        case SOCKET_SEARCH: {
          const params: SOCKET_SYNC_STRUCT<GlobalSearchResult> = value;
          if (params.type === SOCKET_SYNC_TYPE.UPDATE) {
            useGlobalSearchStore
              .getState()
              .onSearchComplete(params.alias, params.payload);
          }
          break;
        }
        case SOCKET_FILTER_ITEMS_TEMPLATE_DETECTION: {
          type TemplateDetection = {
            template_detection_status: TemplateDetectionStatus;
            templates?: TemplateDTO[];
          };
          const params: SOCKET_SYNC_STRUCT<TemplateDetection> = value;
          useFilterItemsStore
            .getState()
            .setTemplateDetection(
              params.payload.template_detection_status,
              params.payload.templates || [],
            );
          break;
        }
        case SOCKET_FILTER_ITEMS_REANALYZE: {
          type ReanalyzeStatusRes = {
            reanalyze_status: ReanalyzeStatus;
          };
          const params: SOCKET_SYNC_STRUCT<ReanalyzeStatusRes> = value;
          useFilterItemsStore
            .getState()
            .setReanalyzeStatus(params.payload.reanalyze_status);
          break;
        }
        case SOCKET_TAXONOMY_PREDICTION_STATE: {
          const params: SOCKET_SYNC_STRUCT<PredictionProgressPayload> = value;
          if (params.payload.prediction_state === PREDICTION_STATE.IDLE) {
            await timeOut(5000);
          }
          updateTaxonomyPredictionState(params.alias, params.payload);
          break;
        }
        case SOCKET_TOPIC2_PREDICTION_STATE: {
          const params: SOCKET_SYNC_STRUCT<PredictionProgressPayload> = value;
          if (params.payload.prediction_state === PREDICTION_STATE.IDLE) {
            await timeOut(5000);
          }
          updateTopicPredictionState(params.alias, params.payload);
          break;
        }
        case SOCKET_CHART_ANALYSIS: {
          const params: SOCKET_SYNC_STRUCT<{}> = value;
          const dashboard2State = useDashboard2Store.getState();
          const { setAnalysisItem: setAnalysisMap } = dashboard2State;

          try {
            const analysis = await service.analysis.fetchAnalysis(params.alias);
            setAnalysisMap(params.alias, analysis);

            // Track chart analysis answer received
            const chart_alias = analysis.reference_id;
            if (chart_alias) {
              const startTime =
                dashboard2State.getAnalysisRequestTime(chart_alias);
              if (startTime) {
                const responseTime = Date.now() - startTime;

                track(AMP_KEYS.CHART_ANALYSIS_ANSWER_RECEIVED, {
                  analysis_alias: analysis.alias,
                  chart_alias: chart_alias,
                  response_time_ms: responseTime,
                });
              }
            }
          } catch {
            console.error(`failed to fetch analysis: ${params.alias}`);
          }

          break;
        }
        case SOCKET_HEY_SYNCLY_CHAT: {
          const params: SOCKET_SYNC_STRUCT<HeySynclyAnswer> = value;
          const payload = params.payload;
          if (payload) {
            useHeySynclyStore.getState().updateAnswerBySocket(payload);
          }
          break;
        }
        case SOCKET_HEY_SYNCLY_STUDIO_ITEM: {
          const params: SOCKET_SYNC_STRUCT<{
            item_alias: Alias;
            status: STUDIO_ITEM_STATUS;
          }> = value;
          const payload = params.payload;
          if (payload) {
            setStudioListUpdateTrigger((prev) => !prev);
          }
          break;
        }
        default:
          console.error(`unknown server msg: ${type}`);
      }
    },
    [
      setStudioListUpdateTrigger,
      updateTaxonomyPredictionState,
      updateTopicPredictionState,
    ],
  );

  return {
    socketOnServerMessageHandler,
  };
};

export { useSocketActions };

export default useSocketStore;
